import React, { useState } from "react";
import { IPaymentDto } from "../../Dtos/PaymentDashboard/PaymentDto";

export const samplePayments = [
    {
        id: 1,
        paymentMethod: "Credit Card",
        receiptId: 5001,
        totalPaidInCents: 1999,
        paymentProvider: "Stripe",
        externalId: "ch_stripe123456",
        paidAt: Date.now() - 30 * 24 * 60 * 60 * 1000, // 30 days ago
        message: "Payment processed successfully"
    },
    {
        id: 2,
        paymentMethod: "PayPal",
        receiptId: 5002,
        totalPaidInCents: 2499,
        paymentProvider: "PayPal",
        externalId: "pay_pal789012",
        paidAt: Date.now() - 60 * 24 * 60 * 60 * 1000, // 60 days ago
        message: ""
    },
    {
        id: 3,
        paymentMethod: "Bank Transfer",
        receiptId: 5003,
        totalPaidInCents: 9999,
        paymentProvider: "Chase",
        externalId: "bnk_trn345678",
        paidAt: Date.now() - 90 * 24 * 60 * 60 * 1000, // 90 days ago
        message: "Verified by bank"
    },
    {
        id: 4,
        paymentMethod: "Apple Pay",
        receiptId: 5004,
        totalPaidInCents: 1499,
        paymentProvider: "Apple",
        externalId: "apl_pay901234",
        paidAt: Date.now() - 120 * 24 * 60 * 60 * 1000, // 120 days ago
        message: "Express checkout"
    },
    {
        id: 5,
        paymentMethod: "Google Pay",
        receiptId: 5005,
        totalPaidInCents: 3499,
        paymentProvider: "Google",
        externalId: "ggl_pay567890",
        paidAt: Date.now() - 150 * 24 * 60 * 60 * 1000, // 150 days ago
        message: ""
    }
];

// Define the IReceiptDto interface
interface IReceiptDto {
    id: number;
    subscriptionTitle: string;
    issuedAt: number;
    paidAt: number;
    totalAmountInCents: number;
    currency: string;
    paymentProvider: string;
    paymentId: string;
    cardNr?: string;
}

// Helper function to format price in cents to currency
const formatPrice = (priceInCents: number, currency = "USD") => {
    return `${(priceInCents / 100).toFixed(2)}`;
};

// Helper function to format timestamp to date
const formatDate = (timestamp: number) => {
    if (!timestamp) return "N/A";
    const date = new Date(timestamp);
    return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
    });
};

export const PaymentsTab = (props: { payments: IPaymentDto[] }) => {
    const { height, width } = window.screen;
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [currentReceipt, setCurrentReceipt] = useState<IReceiptDto | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    // Function to view receipt
    const viewReceipt = async (receiptId: number) => {
        setIsLoading(true);
        console.log("view receipt")
        try {
            // This would be replaced with an actual API call in production
            // const response = await fetch(`/api/receipts/${receiptId}`);
            // const data = await response.json();

            // For demo purposes, we're creating a mock receipt
            const mockReceipt: IReceiptDto = {
                id: receiptId,
                subscriptionTitle: "Premium Subscription",
                issuedAt: Date.now() - 86400000, // Yesterday
                paidAt: Date.now(),
                totalAmountInCents: 2999,
                currency: "USD",
                paymentProvider: "Stripe",
                paymentId: `pay_${Math.random().toString(36).substring(2, 10)}`,
                cardNr: "****-****-****-4242"
            };

            setCurrentReceipt(mockReceipt);
            setIsModalOpen(true);
        } catch (error) {
            console.error("Error fetching receipt:", error);
        } finally {
            setIsLoading(false);
        }
    };

    // Function to send receipt to user
    const sendReceiptToUser = async () => {
        if (!currentReceipt) return;

        // This would be replaced with an actual API call in production
        console.log(`Sending receipt ${currentReceipt.id} to user...`);

        // Show success message
        alert("Receipt has been sent to your email!");
    };

    // Function to print receipt
    const printReceipt = () => {
        if (!currentReceipt) return;

        // Open print dialog
        window.print();
    };

    return (
        <div className="p-6">
            <h3 className="text-lg font-medium text-gray-800 mb-6">Payments</h3>

            {props.payments && props.payments.length > 0 ? (
                <div className="overflow-x-auto shadow-md rounded-lg">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Receipt ID
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Payment Method
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Amount
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Date
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Provider
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    External ID
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {props.payments.map((payment) => (
                                <tr key={payment.id}>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                        {payment.receiptId}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {payment.paymentMethod}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {formatPrice(payment.totalPaidInCents)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {formatDate(payment.paidAt)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {payment.paymentProvider}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {payment.externalId}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        <button
                                            onClick={() => viewReceipt(payment.receiptId)}
                                            disabled={isLoading}
                                            className="text-indigo-600 hover:text-indigo-900 font-medium"
                                        >
                                            View Receipt
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div className="bg-gray-50 p-6 rounded-lg text-center">
                    <p className="text-gray-600">No payment records found</p>
                </div>
            )}

            {/* Receipt Modal */}
            {isModalOpen && currentReceipt && (
                <div className="fixed inset-0 overflow-y-auto z-50 flex items-center justify-center" style={{
                    zIndex: 9999,
                    height,
                    marginLeft: width / 4,
                    width: (width / 2),
                    top: -50,
                    left: 0,
                    marginBottom: 20


                }}  >
                    <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={() => setIsModalOpen(false)}></div>

                    <div className="relative bg-white rounded-lg max-w-lg w-full mx-auto p-6 shadow-xl" style={{ boxShadow: "1px 3px 5px #9E9E9E" }}>
                        <div className="flex justify-between items-start">
                            <h3 className="text-lg font-medium text-gray-900">Receipt #{currentReceipt.id}</h3>
                            <button
                                className="text-gray-400 hover:text-gray-500"
                                onClick={() => setIsModalOpen(false)}
                            >
                                <span className="sr-only">Close</span>
                                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <div className="mt-4">
                            <div className="border-t border-gray-200 py-4">
                                <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                                    <div>
                                        <dt className="text-sm font-medium text-gray-500">Subscription</dt>
                                        <dd className="mt-1 text-sm text-gray-900">{currentReceipt.subscriptionTitle}</dd>
                                    </div>
                                    <div>
                                        <dt className="text-sm font-medium text-gray-500">Total Amount</dt>
                                        <dd className="mt-1 text-sm text-gray-900">
                                            {formatPrice(currentReceipt.totalAmountInCents, currentReceipt.currency)}
                                        </dd>
                                    </div>
                                    <div>
                                        <dt className="text-sm font-medium text-gray-500">Issue Date</dt>
                                        <dd className="mt-1 text-sm text-gray-900">{formatDate(currentReceipt.issuedAt)}</dd>
                                    </div>
                                    <div>
                                        <dt className="text-sm font-medium text-gray-500">Payment Date</dt>
                                        <dd className="mt-1 text-sm text-gray-900">{formatDate(currentReceipt.paidAt)}</dd>
                                    </div>
                                    <div>
                                        <dt className="text-sm font-medium text-gray-500">Payment Provider</dt>
                                        <dd className="mt-1 text-sm text-gray-900">{currentReceipt.paymentProvider}</dd>
                                    </div>
                                    <div>
                                        <dt className="text-sm font-medium text-gray-500">Payment ID</dt>
                                        <dd className="mt-1 text-sm text-gray-900">{currentReceipt.paymentId}</dd>
                                    </div>
                                    {currentReceipt.cardNr && (
                                        <div className="sm:col-span-2">
                                            <dt className="text-sm font-medium text-gray-500">Card Number</dt>
                                            <dd className="mt-1 text-sm text-gray-900">{currentReceipt.cardNr}</dd>
                                        </div>
                                    )}
                                </dl>
                            </div>
                        </div>

                        <div className="mt-6 flex justify-end space-x-3">
                            <button
                                onClick={printReceipt}
                                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                            >
                                Print Receipt
                            </button>
                            <button
                                onClick={sendReceiptToUser}
                                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                            >
                                Email Receipt
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};